import React, { useRef, useReducer, useState, useEffect } from "react";
import { Wizard, WizardPage, IWizardCommonProps } from "cayo.ui.wizards";
import { useIntl } from "react-intl";
import { ChannelName, AccessEntra, SelectUserAccount, Configure } from "../shared";
import { Summary } from "./steps/Summary";
import { communicationChannelsWizardMessages } from "../shared/messages";
import { exchangeOnlineWizardMessages } from "./messages";
import wizardStateReducer, { InitialState } from "./store";
import {
  getConsentUrl,
  exchangeAuthCode,
  promotePermissions,
  getSigninConsentUrl,
  addExchangeOnlineConnector,
} from "../shared/api";
import { openAuthWindowAndGetCode, validateEmails, defineChannelName } from "../shared/helpers";
import { GrantStateType, ConfigEXO } from "../shared/types";
import { endpoints } from "@cayo/shared/services/endpoints.service";
import { logger } from "cayo.ui";

type ActionType = (data: {
  credentialType?: string;
  appUrl: string;
  accountName: string;
}) => Promise<any>;

export const ExchangeOnline = (props: IWizardCommonProps) => {
  const intl = useIntl();
  const wizardRef = useRef<{ resetError: () => void }>(null);
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [loadingText, setLoadingText] = useState<string | undefined>();
  const [wizardState, dispatchWizardState] = useReducer(wizardStateReducer, InitialState);
  const [processing, setProcessing] = useState(false);
  const stateRef = useRef(InitialState);
  stateRef.current = wizardState;

  useEffect(() => {
    defineChannelName(
      "cayo.graph.mailConnector",
      "Exchange Online Communication Channel",
      setLoadingText,
      dispatchWizardState
    );
  }, []);

  const handleChangeField = (form: string, data: any) => {
    dispatchWizardState({
      type: "update",
      payload: {
        form,
        data,
      },
    });
  };

  const handleGrant = (
    accountName: string,
    form: string,
    action: ActionType,
    callback?: (token: string) => void
  ) => {
    setProcessing(true);
    action({
      credentialType: "communicationChannel",
      appUrl: endpoints.redirectUri,
      accountName,
    }).then((res) => {
      if (res?.url) {
        openAuthWindowAndGetCode(res.url)
          .then(({ code, error, error_description }) => {
            if (code) {
              exchangeAuthCode({
                serviceType: "communicationChannel",
                authorizationCode: code,
                accountName,
              }).then((res) => {
                if (res?.value) {
                  setProcessing(false);
                  dispatchWizardState({
                    type: "update",
                    payload: {
                      form,
                      data: {
                        grantState: "Granted",
                        token: res?.value,
                        error: "",
                      },
                    },
                  });

                  dispatchWizardState({
                    type: "update",
                    payload: {
                      form: "grantedAccounts",
                      data: {
                        [form === "adminAccount" ? "admin" : "user"]: accountName,
                      },
                    },
                  });

                  callback && callback(res?.value);
                } else if (res?.message) {
                  setProcessing(false);
                  dispatchWizardState({
                    type: "update",
                    payload: {
                      form,
                      data: {
                        error: res.message,
                      },
                    },
                  });
                }
              });
            } else if (!code && error) {
              setProcessing(false);
              dispatchWizardState({
                type: "update",
                payload: {
                  form,
                  data: {
                    grantState: "Invalid Credentials",
                    error: error_description || "",
                  },
                },
              });
            }
          })
          .catch(() => setProcessing(false));
      } else if (res?.message) {
        setProcessing(false);
        dispatchWizardState({
          type: "update",
          payload: {
            form,
            data: {
              error: res.message,
            },
          },
        });
      }
    });
  };

  return (
    <Wizard
      title={intl.formatMessage(exchangeOnlineWizardMessages.channelName)}
      isFinished={isFinished}
      activePage={0}
      ref={wizardRef}
      showStepError
      closeConfirmationMessage={" "}
      loadingText={loadingText}
      {...props}
    >
      <WizardPage
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(communicationChannelsWizardMessages.wizardTitle)}
        stepName={intl.formatMessage(communicationChannelsWizardMessages.wizardTitle)}
        subTitle=""
        isInputValid={wizardState?.chanelInfo?.name?.trim()}
      >
        <ChannelName
          name={wizardState?.chanelInfo?.name}
          description={wizardState?.chanelInfo?.description}
          handleChange={(key, value) =>
            handleChangeField("chanelInfo", {
              [key]: value,
            })
          }
        />
      </WizardPage>

      <WizardPage
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(communicationChannelsWizardMessages.accessEntra)}
        stepName={intl.formatMessage(communicationChannelsWizardMessages.accessEntra)}
        subTitle=""
        isInputValid={
          wizardState?.adminAccount?.grantState === GrantStateType.granted &&
          wizardState?.adminAccount?.name === wizardState?.grantedAccounts?.admin
        }
      >
        <AccessEntra
          error={wizardState?.adminAccount?.error}
          accountName={wizardState?.adminAccount?.name}
          grantIsActive={!!wizardState?.adminAccount?.name}
          processing={processing}
          accountMissMatch={
            wizardState?.adminAccount?.grantState === GrantStateType.granted &&
            wizardState?.adminAccount?.name !== wizardState?.grantedAccounts?.admin
          }
          onGrant={() =>
            handleGrant(wizardState?.adminAccount?.name, "adminAccount", getConsentUrl, (token) => {
              promotePermissions({
                refreshToken: token,
                accountName: wizardState?.adminAccount?.name,
                credentialType: "communicationChannel",
              }).catch((err) => logger.log(err));
            })
          }
          grantState={wizardState?.adminAccount?.grantState}
          handleChange={(key, value) =>
            handleChangeField("adminAccount", {
              [key]: value,
            })
          }
        />
      </WizardPage>

      <WizardPage
        onNext={() => {
          let sender = stateRef.current?.adminAccount?.name;
          if (stateRef.current?.userAccount?.use) {
            sender = stateRef.current?.userAccount?.name;
          }

          dispatchWizardState({
            type: "update",
            payload: {
              form: "senderAndRecipients",
              data: {
                sender,
              },
            },
          });

          return Promise.resolve();
        }}
        title={intl.formatMessage(communicationChannelsWizardMessages.selectUserAccount)}
        stepName={intl.formatMessage(communicationChannelsWizardMessages.selectUserAccount)}
        subTitle=""
        isInputValid={
          (wizardState?.userAccount?.grantState === GrantStateType.granted &&
            wizardState?.userAccount?.name === wizardState?.grantedAccounts?.user) ||
          !wizardState?.userAccount?.use
        }
      >
        <SelectUserAccount
          error={wizardState?.userAccount?.error}
          accountName={wizardState?.userAccount?.name}
          grantIsActive={!!wizardState?.userAccount?.name}
          processing={processing}
          accountMissMatch={
            wizardState?.userAccount?.use &&
            wizardState?.userAccount?.grantState === GrantStateType.granted &&
            wizardState?.userAccount?.name !== wizardState?.grantedAccounts?.user
          }
          onGrant={() =>
            handleGrant(wizardState?.userAccount?.name, "userAccount", getSigninConsentUrl)
          }
          grantState={wizardState?.userAccount?.grantState}
          handleChange={(key, value) =>
            handleChangeField("userAccount", {
              [key]: value,
            })
          }
          resetError={() => {
            dispatchWizardState({
              type: "update",
              payload: {
                form: "userAccount",
                data: {
                  error: "",
                },
              },
            });
          }}
        />
      </WizardPage>

      <WizardPage
        onNext={() => {
          let data: ConfigEXO = {
            adminAccountName: stateRef.current?.adminAccount?.name,
            adminAccountToken: stateRef.current?.adminAccount?.token,
            sender: stateRef.current?.senderAndRecipients?.sender,
            recipients: stateRef.current?.senderAndRecipients?.recipients,
            sendTestMessage: true,
            name: stateRef.current?.chanelInfo?.name,
            description: stateRef.current?.chanelInfo?.description,
          };

          if (stateRef.current?.userAccount?.use) {
            data = {
              ...data,
              accountName: stateRef.current?.userAccount?.name,
              accountToken: stateRef.current?.userAccount?.token,
            };
          }

          return addExchangeOnlineConnector(data, () => setIsFinished(true));
        }}
        title={intl.formatMessage(communicationChannelsWizardMessages.configureExchangeOnlinePage)}
        stepName={intl.formatMessage(communicationChannelsWizardMessages.configureExchangeOnline)}
        subTitle=""
        isInputValid={
          !validateEmails([wizardState?.senderAndRecipients?.sender]) &&
          wizardState?.senderAndRecipients?.recipients?.length &&
          !validateEmails(wizardState?.senderAndRecipients?.recipients)
        }
      >
        <Configure
          sender={wizardState?.senderAndRecipients?.sender}
          recipients={wizardState?.senderAndRecipients?.recipients}
          handleChange={(key, value) =>
            handleChangeField("senderAndRecipients", {
              [key]: value,
            })
          }
        />
      </WizardPage>

      <WizardPage
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(communicationChannelsWizardMessages.summary)}
        stepName={intl.formatMessage(communicationChannelsWizardMessages.summary)}
        subTitle=""
      >
        <Summary
          channelName={wizardState?.chanelInfo?.name}
          chanelDesc={wizardState?.chanelInfo?.description}
          accountName={
            wizardState?.userAccount?.use
              ? wizardState?.userAccount?.name
              : wizardState?.adminAccount?.name
          }
          sender={wizardState?.senderAndRecipients?.sender}
          recipients={wizardState?.senderAndRecipients?.recipients}
        />
      </WizardPage>
    </Wizard>
  );
};
